/**
 * UGC video carousel: oEmbed title/thumbnail, modal iframe playback, paginated track.
 */
(function () {
  'use strict';

  function parseVideoUrl(raw) {
    if (!raw || typeof raw !== 'string') return null;
    var url = raw.trim();
    // In HTML attributes, URLs may include entities like &amp;
    url = url.replace(/&amp;/g, '&');
    url = url.replace(/&#38;/g, '&');
    // Amazon video detail page (VDP). Not embeddable in an iframe reliably.
    var amz = url.match(/amazon\.(?:com|co\.uk|de|fr|it|es|ca|com\.mx|com\.au|co\.jp|in)\/vdp\/([a-f0-9]{20,})/i);
    if (amz) return { platform: 'amazon', id: amz[1], original: url };
    var m = url.match(
      /(?:youtube\.com\/(?:watch\?(?:[^#]*&)?v=|embed\/|shorts\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/
    );
    if (m) return { platform: 'youtube', id: m[1], original: url };
    m = url.match(/vimeo\.com\/(?:channels\/[^/]+\/|groups\/[^/]+\/videos\/|video\/|)(\d+)/);
    if (m) return { platform: 'vimeo', id: m[1], original: url };
    return null;
  }

  function youtubeThumbUrls(id) {
    return [
      'https://i.ytimg.com/vi/' + id + '/maxresdefault.jpg',
      'https://i.ytimg.com/vi/' + id + '/hqdefault.jpg',
      'https://i.ytimg.com/vi/' + id + '/mqdefault.jpg',
    ];
  }

  function loadImage(src) {
    return new Promise(function (resolve, reject) {
      var img = new Image();
      img.onload = function () {
        resolve(src);
      };
      img.onerror = function () {
        reject(new Error('img'));
      };
      img.src = src;
    });
  }

  function pickWorkingYoutubeThumb(id) {
    var urls = youtubeThumbUrls(id);
    var chain = Promise.reject();
    urls.forEach(function (u) {
      chain = chain.catch(function () {
        return loadImage(u);
      });
    });
    return chain.catch(function () {
      return urls[urls.length - 1];
    });
  }

  function oembedUrl(platform, pageUrl) {
    if (platform === 'youtube') {
      return 'https://www.youtube.com/oembed?format=json&url=' + encodeURIComponent(pageUrl);
    }
    return 'https://vimeo.com/api/oembed.json?url=' + encodeURIComponent(pageUrl);
  }

  function fetchOembed(platform, pageUrl) {
    return fetch(oembedUrl(platform, pageUrl), { credentials: 'omit' })
      .then(function (r) {
        if (!r.ok) throw new Error('oembed');
        return r.json();
      })
      .catch(function () {
        return null;
      });
  }

  function embedSrc(platform, id, useNoCookie) {
    if (platform === 'youtube') {
      var host = useNoCookie ? 'www.youtube-nocookie.com' : 'www.youtube.com';
      return 'https://' + host + '/embed/' + id + '?autoplay=1&rel=0&modestbranding=1';
    }
    return 'https://player.vimeo.com/video/' + id + '?autoplay=1';
  }

  function ensureCardVideoIds(card) {
    var url = card.getAttribute('data-video-url') || '';
    var parsed = parseVideoUrl(url);
    if (!parsed) {
      card.removeAttribute('data-video-platform');
      card.removeAttribute('data-video-id');
      return null;
    }
    card.setAttribute('data-video-platform', parsed.platform);
    card.setAttribute('data-video-id', parsed.id);
    card.setAttribute('data-video-original', parsed.original);
    return parsed;
  }

  function hydrateCard(card) {
    var titleOverride = card.getAttribute('data-title-override');
    var sourceOverride = card.getAttribute('data-source-override');
    var thumbOverride = card.getAttribute('data-thumb-url') || '';
    var img = card.querySelector('.ugc-card__img');
    var titleEl = card.querySelector('.ugc-card__title');
    var sourceEl = card.querySelector('.ugc-card__source');
    var sourceIcon = card.querySelector('.ugc-card__source-icon');

    var parsed = ensureCardVideoIds(card);
    if (!parsed) {
      var rawUrl = card.getAttribute('data-video-url') || '';
      if (titleEl) titleEl.textContent = rawUrl ? 'Unsupported or empty video link' : 'Add a video URL';
      return;
    }

    if (!card.getAttribute('data-ugc-overrides-applied')) {
      card.setAttribute('data-ugc-overrides-applied', '1');
      if (titleOverride && titleEl) {
        titleEl.textContent = titleOverride;
      }
      if (sourceOverride && sourceEl) {
        sourceEl.textContent = sourceOverride;
      }
      if (thumbOverride && img) {
        img.removeAttribute('hidden');
        img.src = thumbOverride;
        img.alt = titleOverride || 'Video thumbnail';
      }
    }

    if (card.getAttribute('data-ugc-fetch-started') === '1') {
      return;
    }
    card.setAttribute('data-ugc-fetch-started', '1');

    // Amazon VDP has no oEmbed and blocks iframe embedding.
    if (parsed.platform === 'amazon') {
      if (titleEl && !titleOverride) titleEl.textContent = 'Amazon video';
      if (sourceEl && !sourceOverride) sourceEl.textContent = 'Amazon';
      return;
    }

    function applyThumbFromUrl(thumb, alt) {
      if (!img || thumbOverride) return;
      if (!thumb) return;
      img.removeAttribute('hidden');
      img.src = thumb;
      img.alt = alt || 'Video thumbnail';
      if (parsed.platform === 'youtube') {
        img.addEventListener(
          'error',
          function onErr() {
            img.removeEventListener('error', onErr);
            pickWorkingYoutubeThumb(parsed.id).then(function (src) {
              img.src = src;
            });
          },
          { once: true }
        );
      }
    }

    fetchOembed(parsed.platform, parsed.original).then(function (data) {
      if (!data) {
        if (parsed.platform === 'youtube' && img && !thumbOverride) {
          pickWorkingYoutubeThumb(parsed.id).then(function (src) {
            img.removeAttribute('hidden');
            img.src = src;
            img.alt = titleOverride || 'Video thumbnail';
          });
        }
        if (titleEl && !titleOverride) titleEl.textContent = 'Video review';
        return;
      }

      applyThumbFromUrl(data.thumbnail_url, data.title);

      if (parsed.platform === 'youtube' && img && !thumbOverride && !data.thumbnail_url) {
        pickWorkingYoutubeThumb(parsed.id).then(function (src) {
          img.removeAttribute('hidden');
          img.src = src;
          img.alt = data.title || 'Video thumbnail';
        });
      }

      if (titleEl && !titleOverride && data.title) titleEl.textContent = data.title;

      if (sourceEl && !sourceOverride && data.author_name) {
        sourceEl.textContent = data.author_name;
      }

      if (data.author_url && sourceIcon && sourceIcon.getAttribute('data-custom-logo') !== 'true') {
        try {
          var u = new URL(data.author_url);
          sourceIcon.src = 'https://www.google.com/s2/favicons?domain=' + encodeURIComponent(u.hostname) + '&sz=32';
          sourceIcon.hidden = false;
        } catch (e) {
          /* ignore */
        }
      }
    });
  }

  function getVisibleCount(viewport) {
    var w = viewport.offsetWidth;
    if (w <= 640) return 1;
    if (w <= 1100) return 2;
    return 3;
  }

  function initCarousel(section) {
    var viewport = section.querySelector('[data-ugc-viewport]');
    var track = section.querySelector('[data-ugc-track]');
    var prevBtn = section.querySelector('[data-ugc-prev]');
    var nextBtn = section.querySelector('[data-ugc-next]');
    var progress = section.querySelector('[data-ugc-progress]');
    if (!viewport || !track || !prevBtn || !nextBtn) return;

    var slides = track.querySelectorAll('.ugc-video-carousel__slide');
    if (!slides.length) return;

    var currentPage = 0;

    function getGap() {
      var g = getComputedStyle(track).gap;
      var n = parseFloat(g);
      return isNaN(n) ? 16 : n;
    }

    function getSlideStep() {
      var slide = slides[0];
      if (!slide) return 0;
      return slide.offsetWidth + getGap();
    }

    function buildProgress(pages) {
      if (!progress) return;
      progress.innerHTML = '';
      for (var i = 0; i < pages; i++) {
        var seg = document.createElement('span');
        seg.className = 'ugc-video-carousel__progress-seg';
        if (i === currentPage) seg.classList.add('is-active');
        seg.setAttribute('role', 'presentation');
        progress.appendChild(seg);
      }
    }

    function update() {
      var visible = getVisibleCount(viewport);
      var pages = Math.max(1, Math.ceil(slides.length / visible));
      if (currentPage > pages - 1) currentPage = pages - 1;

      var step = getSlideStep();
      var offset = step * currentPage * visible;
      track.style.transform = 'translateX(-' + offset + 'px)';

      prevBtn.disabled = currentPage === 0;
      nextBtn.disabled = currentPage >= pages - 1;

      buildProgress(pages);
    }

    prevBtn.addEventListener('click', function () {
      if (currentPage > 0) {
        currentPage -= 1;
        update();
      }
    });
    nextBtn.addEventListener('click', function () {
      var visible = getVisibleCount(viewport);
      var pages = Math.max(1, Math.ceil(slides.length / visible));
      if (currentPage < pages - 1) {
        currentPage += 1;
        update();
      }
    });
    window.addEventListener('resize', update);
    update();
  }

  function initModal(section) {
    var modal = section.querySelector('[data-ugc-modal]');
    var backdrop = section.querySelector('[data-ugc-modal-backdrop]');
    var closeBtns = section.querySelectorAll('[data-ugc-modal-close]');
    var iframe = section.querySelector('[data-ugc-modal-iframe]');
    if (!modal || !iframe) return;

    var useNoCookie = section.getAttribute('data-youtube-nocookie') === 'true';

    function closeModal() {
      modal.hidden = true;
      iframe.removeAttribute('src');
      document.body.classList.remove('ugc-video-modal-open');
    }

    function openFromCard(card) {
      if (!ensureCardVideoIds(card)) return;
      hydrateCard(card);
      var platform = card.getAttribute('data-video-platform');
      var id = card.getAttribute('data-video-id');
      var original = card.getAttribute('data-video-original') || card.getAttribute('data-video-url') || '';
      if (!platform || !id) return;
      if (platform === 'amazon') {
        // Amazon blocks iframe playback; open in a new tab as a fallback.
        if (original) window.open(original, '_blank', 'noopener');
        return;
      }
      iframe.src = embedSrc(platform, id, useNoCookie);
      modal.hidden = false;
      document.body.classList.add('ugc-video-modal-open');
      var closeBtn = modal.querySelector('[data-ugc-modal-close]');
      if (closeBtn) closeBtn.focus();
    }

    section.querySelectorAll('[data-ugc-open-modal]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var card = btn.closest('.ugc-card');
        if (card) openFromCard(card);
      });
    });

    if (backdrop) backdrop.addEventListener('click', closeModal);
    closeBtns.forEach(function (b) {
      b.addEventListener('click', closeModal);
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && !modal.hidden) closeModal();
    });
  }

  function observeCardHydration(section) {
    var cards = section.querySelectorAll('.ugc-card');
    if (!cards.length) return;
    if (!('IntersectionObserver' in window)) {
      cards.forEach(function (card) {
        hydrateCard(card);
      });
      return;
    }
    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (en) {
          if (!en.isIntersecting) return;
          hydrateCard(en.target);
          io.unobserve(en.target);
        });
      },
      { root: null, rootMargin: '120px 0px', threshold: 0.01 }
    );
    cards.forEach(function (card) {
      io.observe(card);
    });
  }

  function initSection(section) {
    observeCardHydration(section);
    initCarousel(section);
    initModal(section);
  }

  function run() {
    document.querySelectorAll('[data-ugc-video-section]').forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }

  document.addEventListener('shopify:section:load', function (e) {
    var root = e.target && e.target.querySelector ? e.target.querySelector('[data-ugc-video-section]') : null;
    if (root) initSection(root);
  });
})();
